/**
 * DV-TEMP-002/005: useHeartbeats Hook
 *
 * React Query hook for fetching execution heartbeats with stage status.
 * Updated for DV-TEMP-005 to use demo endpoints with proper headers.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { buildApiUrl, API_ENDPOINTS, getApiHeadersGet } from '@/lib/api-base';
import { executionKeys, type ExecutionHeartbeats, APIClientError } from '@/lib/api-types';

interface UseHeartbeatsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useHeartbeats(
  executionId: string,
  options: UseHeartbeatsOptions = {}
): UseQueryResult<ExecutionHeartbeats, APIClientError> {
  const { enabled = true, refetchInterval = 5000 } = options; // Default 5s refresh for heartbeats

  return useQuery({
    queryKey: executionKeys.heartbeats(executionId),
    queryFn: async (): Promise<ExecutionHeartbeats> => {
      // DV-TEMP-005: Use demo endpoint with preflight-safe headers
      const url = buildApiUrl(API_ENDPOINTS.demo.heartbeats(executionId));

      const response = await fetch(url, {
        credentials: 'include',
        headers: getApiHeadersGet(), // No Content-Type for GET requests
        cache: 'no-store', // API-first policy
      });

      // Capture X-Request-Id for correlation
      const requestId = response.headers.get('X-Request-Id');

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`
        }));

        throw new APIClientError(
          error.message || `Failed to fetch heartbeats`,
          response.status,
          error.code,
          { ...error.details, requestId }
        );
      }

      const data = await response.json();

      // Attach request ID to response for logging/debugging
      if (requestId && typeof data === 'object') {
        data._requestId = requestId;
      }

      return data;
    },
    enabled: enabled && !!executionId,
    refetchInterval,
    staleTime: 2 * 1000, // 2 seconds - heartbeats should be fresh
    gcTime: 30 * 1000, // 30 seconds - clean up old heartbeat data quickly
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (execution not found) or 403 (unauthorized)
      if (error?.status === 404 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Utility hook to get heartbeat for a specific stage
 */
export function useStageHeartbeat(executionId: string, stage: string) {
  const { data: heartbeats, ...queryResult } = useHeartbeats(executionId);

  const stageHeartbeat = heartbeats?.heartbeats.find(hb => hb.stage === stage);

  return {
    ...queryResult,
    heartbeat: stageHeartbeat,
    isActive: stageHeartbeat?.isActive || false,
    progress: stageHeartbeat?.progress || 0,
    message: stageHeartbeat?.message || '',
    lastHeartbeat: stageHeartbeat?.lastHeartbeat,
    iteration: stageHeartbeat?.iteration || 0,
  };
}

export default useHeartbeats;